import { useRef, type ReactNode } from 'react';
import type { IconType } from 'react-icons';
import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { MdAutoStories, MdCameraAlt, MdCollections, MdExpandMore, MdInfoOutline } from 'react-icons/md';
import { RiftboundButton } from '../ui/button';
import { menuItemHoverBg, menuItemSelectedBg, menuItemSelectedHoverBg } from '../../theme/recipes/button';
import { dropdownBg, raisedShadow } from '../../theme/themeColor';
import { navigateToCollectionScanner } from '../../scan/scanModalNavigation';
import { useAuth } from '../../hooks/useAuth';
import { usePendingScanTip } from '../../hooks/usePendingScanTip';
import { NavCountBadge } from './NavCountBadge';
import { NavSubmenuPortal, useNavSubmenuHover } from './NavSubmenuPortal';

function SubmenuItemFrame({
  active,
  children,
}: {
  active: boolean;
  children: ReactNode;
}) {
  return (
    <Flex
      role="menuitem"
      align="center"
      gap={2.5}
      px={3}
      py={2}
      fontSize="sm"
      fontWeight={active ? 'semibold' : 'medium'}
      color={active ? 'accent' : 'text'}
      bg={active ? menuItemSelectedBg : 'transparent'}
      transitionProperty="background-color, color"
      transitionDuration="0.12s"
      _hover={{
        bg: active ? menuItemSelectedHoverBg : menuItemHoverBg,
        color: active ? 'accent' : 'text',
      }}
    >
      {children}
    </Flex>
  );
}

function CollectionSubmenuLink({
  to,
  icon,
  active,
  children,
}: {
  to: string;
  icon: IconType;
  active: boolean;
  children: string;
}) {
  return (
    <RouterLink to={to} style={{ display: 'block', textDecoration: 'none', outline: 'none' }}>
      <SubmenuItemFrame active={active}>
        <Icon as={icon} boxSize={4} color={active ? 'accent' : 'textMuted'} flexShrink={0} aria-hidden />
        {children}
      </SubmenuItemFrame>
    </RouterLink>
  );
}

function CollectionSubmenuScanButton({ active }: { active: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <Box
      as="button"
      w="100%"
      textAlign="left"
      cursor="pointer"
      border="none"
      bg="transparent"
      p={0}
      onClick={() => {
        navigateToCollectionScanner(navigate, location);
      }}
    >
      <SubmenuItemFrame active={active}>
        <Icon as={MdCameraAlt} boxSize={4} color={active ? 'accent' : 'textMuted'} flexShrink={0} aria-hidden />
        Scan Cards
      </SubmenuItemFrame>
    </Box>
  );
}

export interface CollectionNavSubmenuProps {
  navMatch: (pathname: string) => boolean;
}

export function CollectionNavSubmenu({ navMatch }: CollectionNavSubmenuProps) {
  const location = useLocation();
  const { user } = useAuth();
  const scanTipCount = usePendingScanTip(location.pathname, Boolean(user));
  const hover = useNavSubmenuHover();
  const anchorRef = useRef<HTMLDivElement>(null);
  const pathname = location.pathname;
  const collectionTo = scanTipCount > 0 ? '/collection?justScanned=1' : '/collection';

  const collectionActive =
    pathname === '/collection' || pathname.startsWith('/collection/sets/');
  const bindersActive = pathname === '/collection/binders' || pathname.startsWith('/collection/binders/');
  const scanActive = pathname === '/collection/scan';
  const landingActive = pathname === '/features/collection';
  // Close the hover menu when scan presents so it doesn't sit above the modal.
  const menuOpen = hover.open && !scanActive;

  return (
    <Box
      ref={anchorRef}
      position="relative"
      onMouseEnter={hover.show}
      onMouseLeave={hover.hide}
    >
      <RiftboundButton
        as={RouterLink as never}
        {...{ to: collectionTo } as Record<string, unknown>}
        treatment="ghost"
        size="sm"
        variant={navMatch(pathname) ? 'primary' : undefined}
        px={{ md: 2, xl: 3 }}
        h="auto"
        py={1.5}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        <Flex align="center" gap={1} lineHeight={1.15} position="relative">
          <Text as="span" fontSize="sm">
            Collection
          </Text>
          <NavCountBadge count={scanTipCount} />
          <Icon as={MdExpandMore} boxSize={3.5} opacity={0.75} pointerEvents="none" aria-hidden />
        </Flex>
      </RiftboundButton>

      <NavSubmenuPortal
        open={menuOpen}
        anchorRef={anchorRef}
        label="Collection submenu"
        minW="232px"
        onMouseEnter={hover.show}
        onMouseLeave={hover.hide}
      >
          <Box
            bg={dropdownBg}
            borderWidth="hairline"
            borderColor="border"
            rounded="md"
            py={1}
            boxShadow={raisedShadow}
          >
            <CollectionSubmenuLink to="/collection" icon={MdCollections} active={collectionActive}>
              My Collection
            </CollectionSubmenuLink>
            <CollectionSubmenuLink to="/collection/binders" icon={MdAutoStories} active={bindersActive}>
              Binder Shelf
            </CollectionSubmenuLink>
            <CollectionSubmenuScanButton active={scanActive} />
            <CollectionSubmenuLink to="/features/collection" icon={MdInfoOutline} active={landingActive}>
              Collection features
            </CollectionSubmenuLink>
        </Box>
      </NavSubmenuPortal>
    </Box>
  );
}
